/**
 * @author        carlos.soto@zumper.com (Carlos J. Soto)
 * @copyright     Copyright (c) 2021, Zumper
 * @description   BannerWrapper.tsx
 */

import { useMountAndUnmount } from '@zcode-ui/hook'
import {
  BetaFeature,
  ZUMPER_APPSFLYER_GENERIC_PID,
  getUtmPageType,
  isBetaFeatureEnabled,
  useConstructAppsFlyerLink,
} from '@zcode/nr-utils'
import * as React from 'react'
import { useLocation } from 'react-router-dom'
import { useConstructFirebaseLink } from '../hooks/useConstructFirebaseLink'
import { isBannerDismissed } from '../module/helpers/isBannerDismissed'
import { AppBanner } from './AppBanner/AppBanner'
import { DetailBanner } from './DetailBanner/DetailBanner'
import { type BANNER_SUFFIXES, BANNER_TYPE } from './constants'

export interface BannerWrapperProps {
  bannerAlreadyShown: boolean
  bannerSuffix?: (typeof BANNER_SUFFIXES)[keyof typeof BANNER_SUFFIXES]
  onClose: () => void
  onMount: () => void
  onUnmount: () => void
  type: keyof typeof BANNER_TYPE
  show: boolean
}

export const BannerWrapper = ({
  bannerAlreadyShown,
  bannerSuffix,
  onClose,
  onMount,
  onUnmount,
  type,
  show,
}: BannerWrapperProps) => {
  useMountAndUnmount({ onMount, onUnmount })
  const [isHidden, setIsHidden] = React.useState(false)
  const firebaseUrl = useConstructFirebaseLink(type, bannerSuffix)
  const utmPageType = getUtmPageType(type)
  const appsFlyerUrl = useConstructAppsFlyerLink(
    utmPageType,
    ZUMPER_APPSFLYER_GENERIC_PID,
    bannerSuffix
  )
  const location = useLocation()
  const url = isBetaFeatureEnabled(location, BetaFeature.APPS_FLYER) ? appsFlyerUrl : firebaseUrl
  const isGallery = type === BANNER_TYPE.GALLERY

  if (!show || bannerAlreadyShown || isBannerDismissed() || !type) {
    return null
  }

  const handleClose = () => {
    setIsHidden(true)
    onClose()
  }

  if (type === BANNER_TYPE.DETAIL || isGallery) {
    return (
      <DetailBanner isGallery={isGallery} handleClose={handleClose} isHidden={isHidden} url={url} />
    )
  }

  return <AppBanner url={url} handleClose={handleClose} isHidden={isHidden} />
}
